/**
 * Glyph atlas for the de-rez pass: a cp437 memory drawn with rectangles and lines only,
 * so nothing depends on a font. White glyphs on black; the shader reads the red channel.
 * Drawn at the exact device-pixel cell size (rebuilt on resize) so dither patterns and
 * strokes land on whole pixels instead of being minified into grey.
 *
 * Slot order is load-bearing — post.ts indexes the atlas by these numbers.
 */
export const GLYPH = {
  SPACE: 0,
  SHADE1: 1, // ░
  SHADE2: 2, // ▒
  SHADE3: 3, // ▓
  FULL: 4, // █
  VBAR: 5, // │
  HBAR: 6, // ─
  SLASH: 7, // ╱
  BSLASH: 8, // ╲
  CROSS: 9, // ┼
  TL: 10, // ┌
  TR: 11, // ┐
  BL: 12, // └
  BR: 13, // ┘
  DOT: 14, // ·
} as const;

/** atlas slots (power of two) */
export const GLYPH_SLOTS = 16;

export function buildGlyphAtlas(W: number, H: number): HTMLCanvasElement {
  W = Math.max(2, Math.round(W));
  H = Math.max(4, Math.round(H));
  const c = document.createElement('canvas');
  c.width = W * GLYPH_SLOTS;
  c.height = H;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.fillStyle = '#fff';
  ctx.strokeStyle = '#fff';
  ctx.lineCap = 'butt';

  const ox = (slot: number) => slot * W;
  // grain of the dither and width of the strokes: one device pixel per 8 px of cell
  const g = Math.max(1, Math.round(W / 8));
  const stroke = g;
  const cx = Math.floor(W / 2) - Math.floor(stroke / 2);
  const cy = Math.floor(H / 2) - Math.floor(stroke / 2);

  // shade blocks on a g px grid, the classic dither densities
  const shade = (slot: number, keep: (gx: number, gy: number) => boolean) => {
    for (let gy = 0; gy * g < H; gy++) {
      for (let gx = 0; gx * g < W; gx++) {
        if (keep(gx, gy)) ctx.fillRect(ox(slot) + gx * g, gy * g, g, g);
      }
    }
  };
  shade(GLYPH.SHADE1, (gx, gy) => (gx + 2 * gy) % 4 === 0);
  shade(GLYPH.SHADE2, (gx, gy) => (gx + gy) % 2 === 0);
  shade(GLYPH.SHADE3, (gx, gy) => (gx + 2 * gy) % 4 !== 0);
  ctx.fillRect(ox(GLYPH.FULL), 0, W, H);

  // box-drawing strokes through the cell centre so they join across cells
  const vbar = (slot: number, y0: number, y1: number) => ctx.fillRect(ox(slot) + cx, y0, stroke, y1 - y0);
  const hbar = (slot: number, x0: number, x1: number) => ctx.fillRect(ox(slot) + x0, cy, x1 - x0, stroke);
  vbar(GLYPH.VBAR, 0, H);
  hbar(GLYPH.HBAR, 0, W);
  vbar(GLYPH.CROSS, 0, H); hbar(GLYPH.CROSS, 0, W);
  // corners: named for the corner the strokes leave from
  hbar(GLYPH.TL, cx, W); vbar(GLYPH.TL, cy, H);
  hbar(GLYPH.TR, 0, cx + stroke); vbar(GLYPH.TR, cy, H);
  hbar(GLYPH.BL, cx, W); vbar(GLYPH.BL, 0, cy + stroke);
  hbar(GLYPH.BR, 0, cx + stroke); vbar(GLYPH.BR, 0, cy + stroke);

  // diagonals corner to corner so they chain across cells
  ctx.lineWidth = stroke * 1.2;
  const diag = (slot: number, fromTopLeft: boolean) => {
    ctx.beginPath();
    if (fromTopLeft) { ctx.moveTo(ox(slot), 0); ctx.lineTo(ox(slot) + W, H); }
    else { ctx.moveTo(ox(slot), H); ctx.lineTo(ox(slot) + W, 0); }
    ctx.stroke();
  };
  diag(GLYPH.SLASH, false);
  diag(GLYPH.BSLASH, true);

  // middle dot: 2 px in an 8 px cell, as the VGA font drew it
  const dot = Math.max(1, Math.round(W / 4));
  ctx.fillRect(ox(GLYPH.DOT) + Math.floor((W - dot) / 2), Math.floor((H - dot) / 2), dot, dot);

  return c;
}
